import React, { useState, useEffect } from 'react';
import { auditLogAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './AuditLog.css';

function AuditLog() {
  const { isAdmin } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [filters, setFilters] = useState({
    action: '',
    entityType: '',
    username: '',
    startDate: '',
    endDate: '',
    search: ''
  });
  const [actions, setActions] = useState([]);

  useEffect(() => {
    if (isAdmin()) {
      fetchActions();
      fetchLogs();
    }
  }, []);

  useEffect(() => {
    if (isAdmin()) {
      fetchLogs();
    }
  }, [pagination.page, pagination.limit]);

  const fetchActions = async () => {
    try {
      const response = await auditLogAPI.getActions();
      setActions(response.data);
    } catch (error) {
      console.error('Error fetching actions:', error);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await auditLogAPI.getAll(params);
      setLogs(response.data.logs);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching logs:', error);
      alert('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleApplyFilters = () => {
    setPagination({ ...pagination, page: 1 });
    fetchLogs();
  };

  const handleResetFilters = () => {
    setFilters({
      action: '',
      entityType: '',
      username: '',
      startDate: '',
      endDate: '',
      search: ''
    });
    setPagination({ ...pagination, page: 1 });
    setTimeout(fetchLogs, 0);
  };

  const handlePageChange = (newPage) => {
    setPagination({ ...pagination, page: newPage });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionBadgeClass = (action) => {
    if (action.includes('CREATE')) return 'badge-success';
    if (action.includes('UPDATE')) return 'badge-warning';
    if (action.includes('DELETE')) return 'badge-danger';
    if (action.includes('LOGIN')) return 'badge-info';
    if (action.includes('DOWNLOAD') || action.includes('PRINT')) return 'badge-secondary';
    return 'badge-default';
  };

  if (!isAdmin()) {
    return (
      <div className="access-denied">
        <h2>Access Denied</h2>
        <p>You do not have permission to access this page.</p>
      </div>
    );
  }

  if (loading && logs.length === 0) {
    return <div className="loading">Loading audit logs...</div>;
  }

  return (
    <div className="audit-log">
      <div className="page-header">
        <h2>Activity Log</h2>
        <p className="subtitle">Track all user actions and system changes</p>
      </div>

      <div className="filters-container">
        <div className="filters-row">
          <div className="filter-group">
            <label htmlFor="search">Search</label>
            <input
              type="text"
              id="search"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search username, entity, or details..."
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label htmlFor="action">Action Type</label>
            <select
              id="action"
              name="action"
              value={filters.action}
              onChange={handleFilterChange}
              className="filter-select"
            >
              <option value="">All Actions</option>
              {actions.map(action => (
                <option key={action} value={action}>{action.replace('_', ' ')}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="entityType">Entity Type</label>
            <select
              id="entityType"
              name="entityType"
              value={filters.entityType}
              onChange={handleFilterChange}
              className="filter-select"
            >
              <option value="">All Types</option>
              <option value="User">User</option>
              <option value="Customer">Customer</option>
              <option value="Invoice">Invoice</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              name="username"
              value={filters.username}
              onChange={handleFilterChange}
              placeholder="Filter by username..."
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label htmlFor="startDate">Start Date</label>
            <input
              type="date"
              id="startDate"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label htmlFor="endDate">End Date</label>
            <input
              type="date"
              id="endDate"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              className="filter-input"
            />
          </div>
        </div>

        <div className="filter-actions">
          <button onClick={handleApplyFilters} className="btn btn-primary">
            Apply Filters
          </button>
          <button onClick={handleResetFilters} className="btn btn-secondary">
            Reset
          </button>
        </div>
      </div>

      <div className="logs-count">
        Showing {logs.length} of {pagination.total} log entries
      </div>

      <div className="table-container">
        <table className="data-table audit-table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>User</th>
              <th>Action</th>
              <th>Entity Type</th>
              <th>Entity Name</th>
              <th>Details</th>
              <th>IP Address</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">
                  No audit logs found
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <tr key={log._id}>
                  <td className="timestamp">{formatDate(log.createdAt)}</td>
                  <td className="username">{log.username}</td>
                  <td>
                    <span className={`badge ${getActionBadgeClass(log.action)}`}>
                      {log.action.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td>{log.entityType || '-'}</td>
                  <td>{log.entityName || '-'}</td>
                  <td className="details">{log.details || '-'}</td>
                  <td className="ip-address">{log.ipAddress || '-'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination.pages > 1 && (
        <div className="pagination">
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
            className="btn btn-secondary btn-small"
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {pagination.page} of {pagination.pages}
          </span>
          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page === pagination.pages}
            className="btn btn-secondary btn-small"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default AuditLog;
